import React, { useMemo } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';

import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { StatsCompactes } from '../components/ui/stats-compactes';
import { EmptyState } from '../components/ui/empty-state';
import { LoadingState } from '../components/ui/loading-state';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useTruckMaintenance } from '../components/truck/useTruckMaintenance';
import MaintenanceTimeline, {
  maintenanceDateLabel,
} from '../components/truck/MaintenanceTimeline';
import MaintenanceCosts from '../components/truck/MaintenanceCosts';
import ReadingsTable from '../components/truck/ReadingsTable';
import { BarrePagination } from '../components/ui/pagination-bar';
import { usePagination } from '../hooks/usePagination';

/**
 * Maintenance d'un camion, en sections.
 *
 * C'était une page à part (`/maintenance/trucks/:id`), avec sa propre
 * identité du camion et ses quatre onglets. La fiche camion `/trucks/:id`
 * porte désormais l'identité, l'immobilisation et les papiers; il ne reste ici
 * que ce que l'atelier sait du véhicule: chronologie, coûts, entretiens à
 * prévoir et relevés. L'ancienne adresse redirige vers l'onglet Maintenance de
 * la fiche, pour que les liens déjà partagés continuent de mener quelque part.
 */

const num = (value) => Number(value || 0);

export function TruckMaintenancePanel({ truckId, enabled = true }) {
  const { data, loading, error, reload } = useTruckMaintenance(truckId, { enabled });

  const latestReading = useMemo(
    () =>
      (data?.readings || []).reduce(
        (best, row) => (!best || num(row.value) > num(best.value) ? row : best),
        null,
      ),
    [data],
  );
  const duePlans = useMemo(
    () => (data?.plans || []).filter((plan) => plan.due_state !== 'ok'),
    [data],
  );

  const pageEntretiens = usePagination(duePlans, { cle: 'entretiens' });

  if (loading) {
    return <LoadingState shape="detail" />;
  }

  if (error || !data) {
    return (
      <EmptyState
        variant="error"
        title="La maintenance n’a pas pu être chargée"
        description="L’historique atelier de ce camion n’est pas disponible. Rien ne doit être décidé sur cet onglet tant qu’il n’a pas répondu."
        actionLabel="Réessayer"
        onAction={reload}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="truck-maintenance-panel">
      <StatsCompactes
        stats={[
          {
            label: latestReading
              ? `Dernier compteur · ${maintenanceDateLabel(latestReading.reading_date)}`
              : 'Dernier compteur',
            valeur: latestReading ? num(latestReading.value) : null,
            unite: latestReading?.reading_type === 'hours' ? 'h' : 'km',
            aide: latestReading ? undefined : 'Aucun relevé',
          },
          {
            label: 'Entretiens à prévoir',
            valeur: duePlans.length,
            ton: duePlans.length ? 'attention' : 'positif',
            aide: 'Plans préventifs échus ou proches',
          },
        ]}
      />

      <section className="space-y-3">
        <h2 className="text-base">Entretiens à prévoir</h2>
        {duePlans.length === 0 ? (
          <EmptyState
            variant="empty"
            icon={ShieldAlert}
            title="Aucun entretien à échéance"
            description="Cette section prévient avant qu’un entretien préventif n’arrive à terme. Aucun plan de ce camion n’entre dans sa fenêtre d’alerte — ou aucun n’a encore été enregistré. Les papiers ont leur propre onglet."
            action={
              <Button size="sm" variant="outline" asChild>
                <Link to="/maintenance">Planifier un entretien</Link>
              </Button>
            }
          />
        ) : (
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <Table stickyFirstColumn>
              <TableHeader>
                <TableRow>
                  <TableHead>Entretien</TableHead>
                  <TableHead>Détail</TableHead>
                  <TableHead>État</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageEntretiens.lignes.map((plan) => (
                  <TableRow key={plan.id}>
                    <TableCell className="font-medium">{plan.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {plan.due_reason || '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={plan.due_state === 'overdue' ? 'destructive' : 'warning'}>
                        {plan.due_state === 'overdue' ? 'En retard' : 'À prévoir'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <BarrePagination pagination={pageEntretiens} nom="entretiens" className="mt-0" />
      </section>

      <section className="space-y-3">
        <h2 className="text-base">Chronologie</h2>
        <MaintenanceTimeline data={data} />
      </section>

      <section className="space-y-3">
        <h2 className="text-base">Coûts cumulés</h2>
        <MaintenanceCosts data={data} />
      </section>

      <section className="space-y-3">
        <h2 className="text-base">Relevés</h2>
        <ReadingsTable readings={data.readings} />
      </section>
    </div>
  );
}

/**
 * `/maintenance/trucks/:truckId` → `/trucks/:truckId?tab=maintenance`.
 * `<Navigate>` ne sait pas recopier un paramètre de route: on le lit ici.
 * Le retour mène à l'atelier, d'où l'on venait.
 */
export function RedirectTruckMaintenance() {
  const { truckId } = useParams();
  const location = useLocation();
  return (
    <Navigate
      to={`/trucks/${truckId}?tab=maintenance`}
      replace
      state={{ from: location.state?.from || '/maintenance' }}
    />
  );
}

export default TruckMaintenancePanel;
